import { useCallback, useMemo, useState } from "react";
import type { IconName } from "../components/ui/icon.tsx";
import type {
  EntityBadgeConfig,
  EntityPoolConfig,
} from "../particles/entity-selector/entity-selector-context.tsx";
import type { Omittable } from "../lib/props.ts";

// --- Filter definition types ---
//
// Entity-pool items are an opaque generic: the hook only repackages pools
// into `entitySelectorConfig` and never reads item internals. The consumer
// that renders entity filters (via DataTableFilterButton's
// `renderEntityFilter`) narrows `TItem` to its own selector item type.

export type SelectFilterDef = Omittable<{
  key: string;
  label: string;
  icon?: IconName;
  type: "select";
  options: { value: string; label: string }[];
  operator?: string;
  multiple?: boolean;
}>;

export type EntityFilterPool<TItem = unknown> = {
  id: string;
  label: string;
  items: TItem[];
  /** Hover row action for the pool's rows, carried into the entity selector
   *  the filter body renders (see EntityBadgeConfig — the badge can carry
   *  its own `onAction`). */
  itemBadge?: EntityBadgeConfig | undefined;
} & Pick<
  EntityPoolConfig,
  "total" | "onLoadRange" | "onSearchChange" | "serverFiltered" | "resolveItem"
>;

export type EntityFilterDef<TItem = unknown> = Omittable<{
  key: string;
  label: string;
  icon?: IconName;
  type: "entity";
  pools: EntityFilterPool<TItem>[];
  placeholder?: string;
  operator?: string;
  filterFormat?: "idArray" | "entityRef";
}>;

export type DateFilterDef = Omittable<{
  key: string;
  label: string;
  icon?: IconName;
  type: "date";
  presets?: { value: string; label: string; days: number }[];
}>;

export type FilterDef<TItem = unknown> =
  | SelectFilterDef
  | EntityFilterDef<TItem>
  | DateFilterDef;

// --- Computed filter view ---

export type EntitySelectorFilterConfig<TItem = unknown> = {
  pools: EntityFilterPool<TItem>[];
  searchPlaceholder: string;
};

export type TableFilter<TItem = unknown> = Omittable<{
  key: string;
  label: string;
  icon?: IconName;
  type: "select" | "entity" | "date";
  values: string[];
  isActive: boolean;
  multiple: boolean;
  options?: { value: string; label: string }[];
  entitySelectorConfig?: EntitySelectorFilterConfig<TItem>;
  entityFilterFormat?: "idArray" | "entityRef";
  datePresets?: { value: string; label: string; days: number }[];
}>;

export type UseFilterStateResult<TItem = unknown> = {
  filters: TableFilter<TItem>[];
  activeFilterCount: number;
  clearAll: () => void;
  setFilterValues: (key: string, values: string[]) => void;
  /** Raw filter values — consumers use this to build domain-specific queries. */
  filterValues: Record<string, string[]>;
};

// --- Constants ---

export const DEFAULT_DATE_PRESETS = [
  { value: "1", label: "Last 24 hours", days: 1 },
  { value: "3", label: "Last 3 days", days: 3 },
  { value: "7", label: "Last 7 days", days: 7 },
  { value: "30", label: "Last 30 days", days: 30 },
  { value: "90", label: "Last 90 days", days: 90 },
  { value: "180", label: "Last 6 months", days: 180 },
  { value: "365", label: "Last year", days: 365 },
];

// --- Hook ---

export function useFilterState<TItem = unknown>(
  filterDefs: FilterDef<TItem>[],
): UseFilterStateResult<TItem> {
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(
    {},
  );

  const setValuesForKey = useCallback((key: string, values: string[]) => {
    setFilterValues((prev) => ({ ...prev, [key]: values }));
  }, []);

  const clearAll = useCallback(() => {
    setFilterValues({});
  }, []);

  const filters = useMemo<TableFilter<TItem>[]>(() => {
    return filterDefs.map((def) => {
      const values = filterValues[def.key] ?? [];

      let entitySelectorConfig: EntitySelectorFilterConfig<TItem> | undefined;
      if (def.type === "entity") {
        entitySelectorConfig = {
          pools: def.pools.map(({ itemBadge, ...pool }) => pool),
          searchPlaceholder:
            def.placeholder ?? `Search ${def.label.toLowerCase()}...`,
        };
      }

      return {
        key: def.key,
        label: def.label,
        icon: def.icon,
        type: def.type,
        values,
        isActive: values.length > 0,
        multiple:
          def.type === "select" ? (def.multiple ?? true) : def.type === "entity",
        options: def.type === "select" ? def.options : undefined,
        entitySelectorConfig,
        entityFilterFormat:
          def.type === "entity" ? (def.filterFormat ?? "idArray") : undefined,
        datePresets:
          def.type === "date" ? (def.presets ?? DEFAULT_DATE_PRESETS) : undefined,
      };
    });
  }, [filterDefs, filterValues]);

  const activeFilterCount = useMemo(
    () => filters.filter((f) => f.isActive).length,
    [filters],
  );

  return useMemo(
    () => ({
      filters,
      activeFilterCount,
      clearAll,
      setFilterValues: setValuesForKey,
      filterValues,
    }),
    [filters, activeFilterCount, clearAll, setValuesForKey, filterValues],
  );
}
